import { useMemo } from 'react';
import type { ComponentType } from 'react';
import compose from 'lodash.flowright';
import { withTranslation } from 'react-i18next';
import EnabledCountriesQuery from 'graphqlDocuments/queries/EnabledCountries.graphql';
import { EnabledCountries_Query } from 'graphqlDocuments/queries/__generated__/EnabledCountries';
import useMe from 'utils/hooks/useMe/useMe';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client/react';

type CountryOption = {
  countryId: string;
  phoneRegex: string | null;
  label: string;
  value: string;
};

const getOptions = (
  countries: Array<{
    id: string;
    phoneCode: string;
    translationTag: string;
    phoneRegex: string | null;
  }>,
  t: (s: string) => string,
) =>
  countries
    .map((obj) => ({
      countryId: obj.id,
      phoneRegex: obj.phoneRegex,
      label: `${t(obj.translationTag)} ${obj.phoneCode}`,
      value: obj.phoneCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const getInitialOption = (
  options: Array<CountryOption>,
  args: { countryId: string; phoneCountryCode: string },
) => {
  let optionCountryId = args.countryId;

  if (args.phoneCountryCode) {
    /* option's value is a country phone code */
    const selectedByPhoneCode = options.find((option) => option.value === args.phoneCountryCode);
    if (args.countryId !== selectedByPhoneCode?.countryId) {
      if (args.phoneCountryCode === '+1') {
        optionCountryId = 'USA';
      } else if (selectedByPhoneCode) {
        optionCountryId = selectedByPhoneCode.countryId;
      }
    }
  }

  return options.find((option) => option.countryId === optionCountryId) || null;
};

interface WithOptionsInnerProps {
  countryCodePlaceholder: string;
  data: {
    initialOption: CountryOption | null;
    options: CountryOption[];
  };
  invalid: boolean;
  label: string;
  loading: boolean;
  onChangeCountryCode: (val: any) => void;
  onBlurPhoneNumber: (val: any) => void;
  onChangePhoneNumber: (val: any) => void;
  phoneNumber: string;
  phoneNumberPlaceholder: string;
}

const withOptions = (Component: ComponentType<WithOptionsInnerProps>) => {
  function WithOptionsWrapper(props: any) {
    const {
      country,
      phoneCountryCode,
      t,
      countryCodePlaceholder,
      invalid,
      label,
      onChangeCountryCode,
      onBlurPhoneNumber,
      onChangePhoneNumber,
      phoneNumber,
      phoneNumberPlaceholder,
    } = props;

    const { me: user } = useMe();

    const { data, loading } = useQuery<EnabledCountries_Query>(EnabledCountriesQuery);
    const options = useMemo(() => {
      const countriesTmp = data?.countries?.filter((c) => !!c) ?? [];
      return getOptions(countriesTmp, t);
    }, [data?.countries, t]);
    const initialOption = useMemo(
      () =>
        getInitialOption(options, {
          countryId: country || user?.country || '',
          phoneCountryCode: phoneCountryCode || user?.phoneCountryCode || '',
        }),
      [options, country, user?.country, phoneCountryCode, user?.phoneCountryCode],
    );

    return (
      <Component
        countryCodePlaceholder={countryCodePlaceholder}
        data={{ initialOption, options }}
        invalid={invalid}
        label={label}
        loading={loading}
        onChangeCountryCode={onChangeCountryCode}
        onBlurPhoneNumber={onBlurPhoneNumber}
        onChangePhoneNumber={onChangePhoneNumber}
        phoneNumber={phoneNumber}
        phoneNumberPlaceholder={phoneNumberPlaceholder}
      />
    );
  }
  WithOptionsWrapper.propTypes = {
    country: PropTypes.string.isRequired,
    countryCodePlaceholder: PropTypes.string.isRequired,
    invalid: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    onBlurPhoneNumber: PropTypes.func.isRequired,
    onChangeCountryCode: PropTypes.func.isRequired,
    onChangePhoneNumber: PropTypes.func.isRequired,
    phoneCountryCode: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    phoneNumberPlaceholder: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    user: PropTypes.shape({
      country: PropTypes.string,
      phoneCountryCode: PropTypes.string,
    }).isRequired,
  };
  return WithOptionsWrapper;
};

const SelectCountryPhoneCodeContainer = (SelectCountryPhoneCode: ComponentType<any>) =>
  compose(withTranslation(['COUNTRIES']), withOptions)(SelectCountryPhoneCode);

export default SelectCountryPhoneCodeContainer;
